import { Product } from '@utils/product'

interface ManageItem {
  path: string
  label: string
  root: boolean
  newTab?: boolean
}

interface NavigationSubMenuItem {
  path: string
  label: string
  root?: boolean
  newTab?: boolean
}

interface NavigationSubMenu {
  title: string
  path: string
  items?: NavigationSubMenuItem[]
}

type MobileDrawerMeta = {
  manageItems: ManageItem[]
  productSubMenus: NavigationSubMenu[]
  showStorageLink: boolean
  logout: () => Promise<void>
}

const baseManageItems: ManageItem[] = [
  { path: '/', label: 'Overview', root: true },
  { path: '/api-keys', label: 'API Keys', root: true },
  { path: '/huggingface-tokens', label: 'HuggingFace Tokens', root: true },
  { path: '/invitations', label: 'Invitations', root: true },
  { path: '/organizations', label: 'Organizations', root: true },
  { path: '/user-associations', label: 'User Associations', root: true },
]

const dataQaItems: NavigationSubMenuItem[] = [
  { path: '/', label: 'Overview' },
  { path: '/datasets', label: 'Datasets' },
  { path: '/runs', label: 'Runs' },
]

const unlearnDataItems: NavigationSubMenuItem[] = [
  { path: '/', label: 'Overview' },
  { path: '/models', label: 'Models' },
  { path: '/runs', label: 'Runs' },
]

const getLlmBehaviorsItems = (products: Product[]): NavigationSubMenuItem[] => {
  const items = [{ path: '/', label: 'Overview' }]
  if (
    products.includes(Product.UNLEARNING_LLM_BEHAVIOR) ||
    products.includes(Product.UNLEARNING_LLM_DATA)
  ) {
    items.push({ path: '/llms', label: 'Models' })
    items.push({ path: '/runs', label: 'Runs' })
  }
  if (products.includes(Product.LLM_BEHAVIOR_EVAL)) {
    items.push({ path: '/evals', label: 'Evaluations' })
  }
  return items
}

const buildManageItems = (labelStudioEnabled: boolean) =>
  labelStudioEnabled
    ? [
        ...baseManageItems,
        {
          path: '/label-studio',
          label: 'Label Studio',
          root: true,
          newTab: true,
        },
      ]
    : baseManageItems

const buildProductSubMenus = (products: Product[]) => {
  const subMenus: NavigationSubMenu[] = []
  const dataQaProducts = [
    Product.RADAR_QA,
    Product.VISION_QA,
    Product.SPEECH_QA,
    Product.TABULAR_QA,
    Product.TIMESERIES_QA,
  ]
  const hasDataQa = dataQaProducts.some((product) => products.includes(product))
  const hasUnlearning = products.includes(Product.UNLEARNING_DATA)
  const hasLLM =
    products.includes(Product.UNLEARNING_LLM_BEHAVIOR) ||
    products.includes(Product.UNLEARNING_LLM_DATA) ||
    products.includes(Product.LLM_BEHAVIOR_EVAL)

  if (hasDataQa && !hasUnlearning && !hasLLM) {
    subMenus.push({ title: 'Datasets', path: '/data-qa/datasets' })
    subMenus.push({ title: 'QA Runs', path: '/data-qa/runs' })
  } else if (hasDataQa) {
    subMenus.push({
      title: 'Data QA',
      path: '/data-qa',
      items: dataQaItems,
    })
  }

  if (hasUnlearning && !hasDataQa && !hasLLM) {
    subMenus.push({ title: 'Models', path: '/unlearn-data/models' })
    subMenus.push({ title: 'Unlearning Runs', path: '/unlearn-data/runs' })
  } else if (hasUnlearning) {
    subMenus.push({
      title: 'Unlearn Data',
      path: '/unlearn-data',
      items: unlearnDataItems,
    })
  }

  if (hasLLM && !hasDataQa && !hasUnlearning) {
    if (
      products.includes(Product.UNLEARNING_LLM_BEHAVIOR) ||
      products.includes(Product.UNLEARNING_LLM_DATA)
    ) {
      subMenus.push({ title: 'Models', path: '/llm/llms' })
      subMenus.push({ title: 'Runs', path: '/llm/runs' })
    }
    if (products.includes(Product.LLM_BEHAVIOR_EVAL)) {
      subMenus.push({ title: 'Behavior Evaluations', path: '/llm/evals' })
    }
  } else if (hasLLM) {
    subMenus.push({
      title: 'LLM',
      path: '/llm',
      items: getLlmBehaviorsItems(products),
    })
  }

  return subMenus
}

export {
  buildManageItems,
  buildProductSubMenus,
  type ManageItem,
  type MobileDrawerMeta,
  type NavigationSubMenu,
  type NavigationSubMenuItem,
}
